import { normalizeLocale, type SupportedLocale } from '@/i18n/config';
import { createVietnameseStaticCopy } from '@/i18n/vietnamese-static-copy';
import { getAuthFlowCopy } from './auth-flow-copy';
import {
  getPasswordResetValidationCopy,
  type PasswordResetValidationCopy,
} from './password-reset-validation-copy';

export type AuthPageCopy = PasswordResetValidationCopy & {
  welcomeBack: string;
  letsGetYouBackIn: string;
  email: string;
  emailPlaceholder: string;
  password: string;
  passwordValidation: string;
  forgotPassword: string;
  signIn: string;
  signingIn: string;
  goToHomePage: string;
  checkYourEmail: string;
  verificationCodeSent: string;
  pleaseVerifyEmail: string;
  /** Sign-in refused while the account's deletion is being completed. */
  accountDeletionPending: string;
  verificationCode: string;
  verifying: string;
  verifyAndSignIn: string;
  resendCode: string;
  backToSignIn: string;
  invalidOtp: string;
  enterSixDigitCode: string;
  invalidVerificationCode: string;
  verificationCodeExpired: string;
  invalidCredentials: string;
  emailVerifiedEnterPassword: string;
  emailVerifiedSignInAgain: string;
  invalidPasswordTryAgain: string;
  signInAfterVerificationFailed: string;
  verificationFailed: string;
  accountCreatedCheckEmail: string;
  verificationCodeResentSuccess: string;
  signInFailed: string;
  failedToResendCode: string;
  failedToResendCodeShort: string;
  verificationCodeResent: string;
  orContinueWith: string;
  continueWithGoogle: string;
  googleSignInFailed: string;
  authConfigError: string;
  dontHaveAccount: string;
  signUp: string;
  reactivateAccount: string;
  reactivationOtpDescription: string;
  reactivationPromptDescription: string;
  emailWithValue: string;
  reactivationCode: string;
  reactivating: string;
  reactivateAndSignIn: string;
  reactivationDataNotice: string;
  sendingCode: string;
  sendReactivationCode: string;
  enterReactivationEmail: string;
  failedSendReactivationCode: string;
  reactivationCodeSent: string;
  failedReactivateAccount: string;
  accountReactivated: string;
  deactivationRequestedNotice: string;
  loggingYouIn: string;
  authSessionWait: string;
  selectedLanguageApplyFailed: string;
  signupCreditsApplyFailed: string;
  signingYouOut: string;
  signOutWait: string;
  signOutFailedTitle: string;
  signOutFailedDescription: string;
  tryAgain: string;
  goBackHome: string;
  goBack: string;
  termsOfUse: string;
  privacyPolicy: string;
  // Sign-up page
  createYourAccount: string;
  enterYourEmailToContinue: string;
  setNamePasswordToContinue: string;
  enterFullNameAndPassword: string;
  continueButton: string;
  alreadyHaveAccount: string;
  emailAddressLabel: string;
  fullName: string;
  confirmPasswordLabel: string;
  createPasswordPlaceholder: string;
  reEnterPasswordPlaceholder: string;
  passwordMinimumHint: string;
  websiteLanguage: string;
  websiteLanguageOptional: string;
  chooseLanguageLater: string;
  selectLanguage: string;
  searchLanguages: string;
  noLanguagesFound: string;
  enterVerificationCode: string;
};

const EN_COPY: AuthPageCopy = {
  welcomeBack: 'Welcome back',
  letsGetYouBackIn: "Let's get you back in",
  email: 'Email',
  emailPlaceholder: 'you@email.example',
  emailValidation: 'Please enter a valid email address',
  passwordResetMinimumValidation: 'Password must be at least 8 characters',
  confirmPasswordValidation: 'Confirm your new password',
  passwordsDoNotMatch: 'Passwords do not match',
  password: 'Password',
  passwordValidation: 'Password is required',
  forgotPassword: 'Forgot password?',
  signIn: 'Sign In',
  signingIn: 'Signing in...',
  goToHomePage: 'Go to home page',
  checkYourEmail: 'Check your email',
  verificationCodeSent: 'We sent a verification code to',
  pleaseVerifyEmail: 'Please verify your email to continue',
  accountDeletionPending:
    'Your account deletion is still being completed. Contact support if this takes more than two days.',
  verificationCode: 'Verification code',
  verifying: 'Verifying...',
  verifyAndSignIn: 'Verify and Sign In',
  resendCode: 'Resend code',
  backToSignIn: 'Back to sign in',
  invalidOtp: 'Invalid OTP',
  enterSixDigitCode: 'Enter the 6-digit code',
  invalidVerificationCode: 'Invalid verification code',
  verificationCodeExpired:
    'This verification code has expired. Request a new one.',
  invalidCredentials:
    'Invalid email or password. Check your credentials and try again.',
  emailVerifiedEnterPassword:
    'Email verified. Enter your password again to finish signing in.',
  emailVerifiedSignInAgain:
    'Email verified. Sign in once more to finish logging in.',
  invalidPasswordTryAgain:
    'Invalid password. Check the requirements and try again.',
  signInAfterVerificationFailed: 'Sign in failed after verification',
  verificationFailed: 'Verification failed',
  accountCreatedCheckEmail:
    'Account created. Enter the 6-digit code sent to your email.',
  verificationCodeResentSuccess: 'New verification code sent',
  signInFailed: 'Sign in failed',
  failedToResendCode: 'Failed to resend code. Please try again.',
  failedToResendCodeShort: 'Failed to resend code',
  verificationCodeResent: 'Verification code resent!',
  orContinueWith: 'OR CONTINUE WITH',
  continueWithGoogle: 'Continue with Google',
  googleSignInFailed: 'Google sign in failed. Please try again.',
  authConfigError:
    'Authentication configuration error. Please contact support.',
  dontHaveAccount: "Don't have an account?",
  signUp: 'Sign up',
  reactivateAccount: 'Reactivate your account',
  reactivationOtpDescription:
    'Enter the 6-digit code we sent to finish reactivating your account.',
  reactivationPromptDescription:
    'This account is currently deactivated. Verify your email to turn it back on.',
  emailWithValue: 'Email:',
  reactivationCode: 'Reactivation code',
  reactivating: 'Reactivating...',
  reactivateAndSignIn: 'Reactivate and Sign In',
  reactivationDataNotice:
    'Your work is still intact. Reactivation restores access without deleting any of your data.',
  sendingCode: 'Sending code...',
  sendReactivationCode: 'Send Reactivation Code',
  enterReactivationEmail: 'Enter your account email to reactivate it.',
  failedSendReactivationCode: 'Failed to send reactivation code.',
  reactivationCodeSent: 'Reactivation code sent. Check your email.',
  failedReactivateAccount: 'Failed to reactivate your account',
  accountReactivated: 'Account reactivated',
  deactivationRequestedNotice:
    'Account deactivation requested. Sign in again within 14 days to cancel it automatically.',
  loggingYouIn: 'Logging you in',
  authSessionWait: 'Hang tight while we securely authenticate your session.',
  selectedLanguageApplyFailed:
    'We could not apply your selected language. Please try again.',
  signupCreditsApplyFailed:
    'We could not finish applying your signup credits. Please contact support if this continues.',
  signingYouOut: 'Signing you out',
  signOutWait: 'Hang tight while we securely end your session',
  signOutFailedTitle: "We couldn't sign you out",
  signOutFailedDescription:
    'Something went wrong while ending your session. Please try again.',
  tryAgain: 'Try again',
  goBackHome: 'Go back home',
  goBack: 'Back',
  termsOfUse: 'Terms of Use',
  privacyPolicy: 'Privacy Policy',
  createYourAccount: 'Create your account',
  enterYourEmailToContinue: 'Enter your email to continue',
  setNamePasswordToContinue: 'Set your name and password to continue',
  enterFullNameAndPassword:
    'Enter your full name and set your password to continue',
  continueButton: 'Continue',
  alreadyHaveAccount: 'Already have an account?',
  emailAddressLabel: 'Email address',
  fullName: 'Full name',
  confirmPasswordLabel: 'Confirm password',
  createPasswordPlaceholder: 'Create a password',
  reEnterPasswordPlaceholder: 'Re-enter your password',
  passwordMinimumHint: 'Password must be at least 6 characters',
  websiteLanguage: 'Website language',
  websiteLanguageOptional:
    'Optional for now. You can also change this later in settings.',
  chooseLanguageLater: 'Later',
  selectLanguage: 'Select a language',
  searchLanguages: 'Search languages…',
  noLanguagesFound: 'No languages found',
  enterVerificationCode: 'Enter verification code',
};

const VI_COPY: AuthPageCopy = {
  ...createVietnameseStaticCopy(EN_COPY),
  goBack: 'Quay lại',
  termsOfUse: 'Điều khoản sử dụng',
  privacyPolicy: 'Chính sách quyền riêng tư',
  passwordMinimumHint: 'Mật khẩu phải có ít nhất 6 ký tự',
  websiteLanguage: 'Ngôn ngữ trang web',
  websiteLanguageOptional:
    'Hiện tại không bắt buộc. Bạn có thể thay đổi sau trong phần cài đặt.',
  chooseLanguageLater: 'Để sau',
  selectLanguage: 'Chọn ngôn ngữ',
  searchLanguages: 'Tìm kiếm ngôn ngữ…',
  noLanguagesFound: 'Không tìm thấy ngôn ngữ.',
};

function mergeAuthCopy(copy: Partial<AuthPageCopy>): AuthPageCopy {
  return { ...EN_COPY, ...copy };
}

const COPY_BY_LOCALE: Record<SupportedLocale, AuthPageCopy> = {
  en: EN_COPY,
  'pt-BR': {
    welcomeBack: 'Bem-vindo de volta.',
    letsGetYouBackIn: 'Vamos te colocar de volta.',
    email: 'E-mail',
    emailPlaceholder: 'Voc\u00EA@imai.exemplo',
    emailValidation: 'Por favor, digite um endere\u00E7o de e-mail v\u00E1lido',
    passwordResetMinimumValidation: 'A senha deve ter pelo menos 8 caracteres.',
    confirmPasswordValidation: 'Confirme sua nova senha.',
    passwordsDoNotMatch: 'Senhas n\u00E3o batem.',
    password: 'Senha',
    passwordValidation: 'A senha \u00E9 necess\u00E1ria.',
    forgotPassword: 'Esqueceu a senha?',
    signIn: 'Entre.',
    signingIn: 'Assinando...',
    goToHomePage: 'V\u00E1 para a p\u00E1gina inicial.',
    checkYourEmail: 'Verifique seu e-mail.',
    verificationCodeSent:
      'Enviamos um c\u00F3digo de verifica\u00E7\u00E3o para',
    pleaseVerifyEmail: 'Por favor, verifique seu e-mail para continuar.',
    accountDeletionPending:
      'A exclusão da sua conta ainda está sendo concluída. Fale com o suporte se demorar mais de dois dias.',
    verificationCode: 'C\u00F3digo de verifica\u00E7\u00E3o.',
    verifying: 'Verificando...',
    verifyAndSignIn: 'Verifique e entre.',
    resendCode: 'Reenviar c\u00F3digo',
    backToSignIn: 'Voltar para assinar.',
    invalidOtp: 'OTP inv\u00E1lido',
    enterSixDigitCode: 'Digite o c\u00F3digo de 6 d\u00EDgitos.',
    invalidVerificationCode:
      'C\u00F3digo de verifica\u00E7\u00E3o inv\u00E1lido.',
    verificationCodeExpired:
      'Este c\u00F3digo de verifica\u00E7\u00E3o expirou. Pe\u00E7a um novo.',
    invalidCredentials:
      'E-mail ou senha inv\u00E1lidos. Verifique suas credenciais e tente novamente.',
    emailVerifiedEnterPassword:
      'E-mail verificado. Digite sua senha novamente para terminar de assinar.',
    emailVerifiedSignInAgain:
      'E-mail verificado. Assine mais uma vez para terminar de fazer login.',
    invalidPasswordTryAgain:
      'Senha inv\u00E1lida. Verifique os requisitos e tente novamente.',
    signInAfterVerificationFailed:
      'O registro falhou ap\u00F3s a verifica\u00E7\u00E3o.',
    verificationFailed: 'A verifica\u00E7\u00E3o falhou.',
    accountCreatedCheckEmail:
      'Conta criada. Digite o c\u00F3digo de 6 d\u00EDgitos enviado para seu e-mail.',
    verificationCodeResentSuccess:
      'Novo c\u00F3digo de verifica\u00E7\u00E3o enviado.',
    signInFailed: 'O sinal falhou.',
    failedToResendCode:
      'N\u00E3o consegui reenviar o c\u00F3digo. Por favor, tente de novo.',
    failedToResendCodeShort: 'N\u00E3o consegui reenviar o c\u00F3digo.',
    verificationCodeResent: 'C\u00F3digo de verifica\u00E7\u00E3o ressentido!',
    orContinueWith: 'OU CONTINUAR COM',
    continueWithGoogle: 'Continue com o Google.',
    googleSignInFailed: 'O sinal do Google falhou. Por favor, tente de novo.',
    authConfigError:
      'Erro de configura\u00E7\u00E3o de autentica\u00E7\u00E3o. Por favor, contate o apoio.',
    dontHaveAccount: 'N\u00E3o tem conta?',
    signUp: 'Assine.',
    reactivateAccount: 'Reativar sua conta.',
    reactivationOtpDescription:
      'Digite o c\u00F3digo de 6 d\u00EDgitos que enviamos para terminar de reativar sua conta.',
    reactivationPromptDescription:
      'Esta conta est\u00E1 desativada. Verifique seu e-mail para ligar de novo.',
    emailWithValue: 'E-mail:',
    reactivationCode: 'C\u00F3digo de reativa\u00E7\u00E3o.',
    reactivating: 'Reativando...',
    reactivateAndSignIn: 'Reativar e iniciar sess\u00E3o',
    reactivationDataNotice:
      'Seu trabalho ainda est\u00E1 intacto. Reativa\u00E7\u00E3o restaura o acesso sem excluir nenhum dos seus dados.',
    sendingCode: 'Enviando c\u00F3digo...',
    sendReactivationCode: 'Enviar C\u00F3digo de Reativa\u00E7\u00E3o',
    enterReactivationEmail: 'Digite seu e-mail de conta para reativ\u00E1-lo.',
    failedSendReactivationCode:
      'Falha em enviar o c\u00F3digo de reativa\u00E7\u00E3o.',
    reactivationCodeSent:
      'C\u00F3digo de reativa\u00E7\u00E3o enviado. Verifique seu e-mail.',
    failedReactivateAccount: 'N\u00E3o conseguiu reativar sua conta.',
    accountReactivated: 'Conta reativada.',
    deactivationRequestedNotice:
      'Desativa\u00E7\u00E3o da conta solicitada. Assine novamente dentro de 14 dias para cancelar automaticamente.',
    loggingYouIn: 'Estou entrando.',
    authSessionWait: 'Aguente firme enquanto autenticamos sua sess\u00E3o.',
    selectedLanguageApplyFailed:
      'N\u00E3o pod\u00EDamos aplicar sua l\u00EDngua selecionada. Por favor, tente de novo.',
    signupCreditsApplyFailed:
      'N\u00E3o pudemos terminar de aplicar seus cr\u00E9ditos de inscri\u00E7\u00E3o. Por favor, contate o apoio se isso continuar.',
    signingYouOut: 'Assinando sua sa\u00EDda',
    signOutWait: 'Aguente firme enquanto encerramos sua sess\u00E3o.',
    signOutFailedTitle: 'N\u00E3o pudemos assinar sua sa\u00EDda.',
    signOutFailedDescription:
      'Algo deu errado enquanto terminava sua sess\u00E3o. Por favor, tente de novo.',
    tryAgain: 'Tente novamente.',
    goBackHome: 'Volte para casa.',
    goBack: 'Voltar',
    termsOfUse: 'Termos de Uso',
    privacyPolicy: 'Pol\u00EDtica de Privacidade',
    createYourAccount: 'Crie sua conta.',
    enterYourEmailToContinue: 'Digite seu e-mail para continuar.',
    setNamePasswordToContinue: 'Defina seu nome e senha para continuar.',
    enterFullNameAndPassword:
      'Digite seu nome completo e defina sua senha para continuar.',
    continueButton: 'Continue.',
    alreadyHaveAccount: 'J\u00E1 tem uma conta?',
    emailAddressLabel: 'E-mail',
    fullName: 'Nome completo.',
    confirmPasswordLabel: 'Confirme a senha.',
    createPasswordPlaceholder: 'Crie uma senha.',
    reEnterPasswordPlaceholder: 'Volte a colocar sua senha.',
    passwordMinimumHint: 'A senha deve ter pelo menos 6 caracteres.',
    websiteLanguage: 'L\u00EDngua do site',
    websiteLanguageOptional:
      'Opcional por enquanto. Voc\u00EA tamb\u00E9m pode mudar isso depois.',
    chooseLanguageLater: 'Mais tarde.',
    selectLanguage: 'Selecione um idioma',
    searchLanguages: 'Procure l\u00EDnguas...',
    noLanguagesFound: 'Nenhuma l\u00EDngua encontrada.',
    enterVerificationCode: 'Digite o c\u00F3digo de verifica\u00E7\u00E3o.',
  },
  ar: mergeAuthCopy({
    welcomeBack: 'مرحبًا بعودتك',
    letsGetYouBackIn: 'لنعيدك إلى حسابك.',
    email: 'البريد الإلكتروني',
    emailPlaceholder: 'you@email.example',
    password: 'كلمة المرور',
    forgotPassword: 'هل نسيت كلمة المرور؟',
    signIn: 'تسجيل الدخول',
    signingIn: 'جارٍ تسجيل الدخول...',
    orContinueWith: 'أو تابع باستخدام',
    continueWithGoogle: 'تابع باستخدام Google',
    dontHaveAccount: 'ليس لديك حساب؟',
    signUp: 'إنشاء حساب',
    signingYouOut: 'جارٍ تسجيل خروجك',
    signOutWait: 'انتظر لحظة بينما ننهي جلستك بأمان.',
    signOutFailedTitle: 'تعذر تسجيل خروجك',
    signOutFailedDescription:
      'حدث خطأ أثناء إنهاء جلستك. يرجى المحاولة مرة أخرى.',
    tryAgain: 'حاول مرة أخرى',
    goBackHome: 'العودة إلى الصفحة الرئيسية',
    goBack: 'رجوع',
    termsOfUse: 'شروط الاستخدام',
    privacyPolicy: 'سياسة الخصوصية',
    createYourAccount: 'أنشئ حسابك',
    enterYourEmailToContinue: 'أدخل بريدك الإلكتروني للمتابعة.',
    setNamePasswordToContinue: 'حدّد اسمك وكلمة المرور للمتابعة.',
    enterFullNameAndPassword: 'أدخل اسمك الكامل وحدّد كلمة المرور للمتابعة',
    continueButton: 'متابعة',
    alreadyHaveAccount: 'هل لديك حساب بالفعل؟',
    emailAddressLabel: 'عنوان البريد الإلكتروني',
    fullName: 'الاسم الكامل',
    confirmPasswordLabel: 'تأكيد كلمة المرور',
    createPasswordPlaceholder: 'أنشئ كلمة مرور',
    reEnterPasswordPlaceholder: 'أعد إدخال كلمة المرور',
    passwordMinimumHint: 'يجب أن تتكون كلمة المرور من 6 أحرف على الأقل',
    websiteLanguage: 'لغة الموقع',
    websiteLanguageOptional: 'اختياري الآن. يمكنك تغييره لاحقًا من الإعدادات.',
    chooseLanguageLater: 'لاحقًا',
    selectLanguage: 'اختر لغة',
    searchLanguages: 'البحث عن اللغات…',
    noLanguagesFound: 'لم يتم العثور على لغات.',
  }),
  es: mergeAuthCopy({
    welcomeBack: 'Bienvenido de nuevo',
    letsGetYouBackIn: 'Vamos a iniciar sesión.',
    email: 'Email',
    emailPlaceholder: 'tu@imai.example',
    password: 'Contraseña',
    forgotPassword: '¿Olvidaste tu contraseña?',
    signIn: 'Iniciar sesión',
    signingIn: 'Iniciando sesión...',
    orContinueWith: 'O CONTINÚA CON',
    continueWithGoogle: 'Continuar con Google',
    dontHaveAccount: '¿No tienes una cuenta?',
    signUp: 'Regístrate',
    signingYouOut: 'Cerrando sesión',
    signOutWait: 'Espera mientras cerramos tu sesión de forma segura.',
    signOutFailedTitle: 'No pudimos cerrar tu sesión',
    signOutFailedDescription:
      'Algo salió mal al cerrar tu sesión. Inténtalo de nuevo.',
    tryAgain: 'Intentar de nuevo',
    goBackHome: 'Volver al inicio',
    goBack: 'Atrás',
    termsOfUse: 'Términos de uso',
    privacyPolicy: 'Política de privacidad',
    createYourAccount: 'Crea tu cuenta',
    enterYourEmailToContinue: 'Introduce tu email para continuar.',
    setNamePasswordToContinue: 'Define tu nombre y contraseña para continuar.',
    enterFullNameAndPassword:
      'Introduce tu nombre completo y define tu contraseña para continuar',
    continueButton: 'Continuar',
    alreadyHaveAccount: '¿Ya tienes una cuenta?',
    emailAddressLabel: 'Dirección de email',
    fullName: 'Nombre completo',
    confirmPasswordLabel: 'Confirmar contraseña',
    createPasswordPlaceholder: 'Crea una contraseña',
    reEnterPasswordPlaceholder: 'Vuelve a introducir tu contraseña',
    passwordMinimumHint: 'La contraseña debe tener al menos 6 caracteres',
    websiteLanguage: 'Idioma del sitio web',
    websiteLanguageOptional:
      'Opcional por ahora. Puedes cambiarlo más tarde en ajustes.',
    chooseLanguageLater: 'Más tarde',
    selectLanguage: 'Selecciona un idioma',
    searchLanguages: 'Buscar idiomas…',
    noLanguagesFound: 'No se encontraron idiomas.',
  }),
  fr: mergeAuthCopy({
    welcomeBack: 'Bon retour',
    letsGetYouBackIn: 'Reconnectons-vous.',
    email: 'E-mail',
    emailPlaceholder: 'vous@imai.example',
    password: 'Mot de passe',
    forgotPassword: 'Mot de passe oublié ?',
    signIn: 'Se connecter',
    signingIn: 'Connexion...',
    orContinueWith: 'OU CONTINUER AVEC',
    continueWithGoogle: 'Continuer avec Google',
    dontHaveAccount: 'Vous n’avez pas de compte ?',
    signUp: 'S’inscrire',
    signingYouOut: 'Déconnexion en cours',
    signOutWait: 'Patientez pendant que nous terminons votre session.',
    signOutFailedTitle: 'Impossible de vous déconnecter',
    signOutFailedDescription:
      'Une erreur s’est produite lors de la déconnexion. Veuillez réessayer.',
    tryAgain: 'Réessayer',
    goBackHome: 'Retour à l’accueil',
    goBack: 'Retour',
    termsOfUse: 'Conditions d’utilisation',
    privacyPolicy: 'Politique de confidentialité',
    createYourAccount: 'Créez votre compte',
    enterYourEmailToContinue: 'Saisissez votre e-mail pour continuer.',
    setNamePasswordToContinue:
      'Définissez votre nom et mot de passe pour continuer.',
    enterFullNameAndPassword:
      'Saisissez votre nom complet et définissez votre mot de passe pour continuer',
    continueButton: 'Continuer',
    alreadyHaveAccount: 'Vous avez déjà un compte ?',
    emailAddressLabel: 'Adresse e-mail',
    fullName: 'Nom complet',
    confirmPasswordLabel: 'Confirmer le mot de passe',
    createPasswordPlaceholder: 'Créez un mot de passe',
    reEnterPasswordPlaceholder: 'Saisissez à nouveau votre mot de passe',
    passwordMinimumHint: 'Le mot de passe doit comporter au moins 6 caractères',
    websiteLanguage: 'Langue du site',
    websiteLanguageOptional:
      'Facultatif pour le moment. Vous pourrez le modifier dans les réglages.',
    chooseLanguageLater: 'Plus tard',
    selectLanguage: 'Sélectionnez une langue',
    searchLanguages: 'Rechercher des langues…',
    noLanguagesFound: 'Aucune langue trouvée.',
  }),
  it: mergeAuthCopy({
    welcomeBack: 'Bentornato',
    letsGetYouBackIn: 'Effettuiamo l’accesso.',
    email: 'Email',
    emailPlaceholder: 'tu@imai.example',
    password: 'Password',
    forgotPassword: 'Password dimenticata?',
    signIn: 'Accedi',
    signingIn: 'Accesso in corso...',
    orContinueWith: 'OPPURE CONTINUA CON',
    continueWithGoogle: 'Continua con Google',
    dontHaveAccount: 'Non hai un account?',
    signUp: 'Registrati',
    signingYouOut: 'Disconnessione in corso',
    signOutWait: 'Attendi mentre terminiamo la sessione in modo sicuro.',
    signOutFailedTitle: 'Non siamo riusciti a disconnetterti',
    signOutFailedDescription:
      'Qualcosa è andato storto durante la disconnessione. Riprova.',
    tryAgain: 'Riprova',
    goBackHome: 'Torna alla home',
    goBack: 'Indietro',
    termsOfUse: 'Termini di utilizzo',
    privacyPolicy: 'Informativa sulla privacy',
    createYourAccount: 'Crea il tuo account',
    enterYourEmailToContinue: 'Inserisci la tua email per continuare.',
    setNamePasswordToContinue: 'Imposta nome e password per continuare.',
    enterFullNameAndPassword:
      'Inserisci il tuo nome completo e imposta la password per continuare',
    continueButton: 'Continua',
    alreadyHaveAccount: 'Hai già un account?',
    emailAddressLabel: 'Indirizzo email',
    fullName: 'Nome completo',
    confirmPasswordLabel: 'Conferma password',
    createPasswordPlaceholder: 'Crea una password',
    reEnterPasswordPlaceholder: 'Reinserisci la password',
    passwordMinimumHint: 'La password deve contenere almeno 6 caratteri',
    websiteLanguage: 'Lingua del sito',
    websiteLanguageOptional:
      'Facoltativo per ora. Puoi cambiarlo più avanti nelle impostazioni.',
    chooseLanguageLater: 'Più tardi',
    selectLanguage: 'Seleziona una lingua',
    searchLanguages: 'Cerca lingue…',
    noLanguagesFound: 'Nessuna lingua trovata.',
  }),
  ko: mergeAuthCopy({
    welcomeBack: '다시 오신 것을 환영합니다',
    letsGetYouBackIn: '로그인을 진행할게요.',
    email: '이메일',
    emailPlaceholder: 'you@email.example',
    password: '비밀번호',
    forgotPassword: '비밀번호를 잊으셨나요?',
    signIn: '로그인',
    signingIn: '로그인 중...',
    orContinueWith: '또는 다음으로 계속',
    continueWithGoogle: 'Google로 계속',
    dontHaveAccount: '계정이 없으신가요?',
    signUp: '가입하기',
    signingYouOut: '로그아웃 중',
    signOutWait: '세션을 안전하게 종료하는 동안 잠시 기다려 주세요.',
    signOutFailedTitle: '로그아웃할 수 없습니다',
    signOutFailedDescription:
      '세션 종료 중 문제가 발생했습니다. 다시 시도해 주세요.',
    tryAgain: '다시 시도',
    goBackHome: '홈으로 돌아가기',
    goBack: '뒤로',
    termsOfUse: '이용 약관',
    privacyPolicy: '개인정보 처리방침',
    createYourAccount: '계정 만들기',
    enterYourEmailToContinue: '계속하려면 이메일을 입력하세요.',
    setNamePasswordToContinue: '계속하려면 이름과 비밀번호를 설정하세요.',
    enterFullNameAndPassword:
      '계속하려면 전체 이름을 입력하고 비밀번호를 설정하세요',
    continueButton: '계속',
    alreadyHaveAccount: '이미 계정이 있나요?',
    emailAddressLabel: '이메일 주소',
    fullName: '전체 이름',
    confirmPasswordLabel: '비밀번호 확인',
    createPasswordPlaceholder: '비밀번호 생성',
    reEnterPasswordPlaceholder: '비밀번호를 다시 입력하세요',
    passwordMinimumHint: '비밀번호는 6자 이상이어야 합니다',
    websiteLanguage: '웹사이트 언어',
    websiteLanguageOptional:
      '지금은 선택 사항입니다. 나중에 설정에서 변경할 수 있습니다.',
    chooseLanguageLater: '나중에',
    selectLanguage: '언어 선택',
    searchLanguages: '언어 검색…',
    noLanguagesFound: '언어를 찾을 수 없습니다.',
  }),
  de: mergeAuthCopy({
    welcomeBack: 'Willkommen zurück',
    letsGetYouBackIn: 'Melden wir dich wieder an.',
    email: 'E-Mail',
    emailPlaceholder: 'du@imai.example',
    password: 'Passwort',
    forgotPassword: 'Passwort vergessen?',
    signIn: 'Anmelden',
    signingIn: 'Anmeldung läuft...',
    orContinueWith: 'ODER WEITER MIT',
    continueWithGoogle: 'Mit Google fortfahren',
    dontHaveAccount: 'Noch kein Konto?',
    signUp: 'Registrieren',
    signingYouOut: 'Du wirst abgemeldet',
    signOutWait: 'Bitte warte, während wir deine Sitzung sicher beenden.',
    signOutFailedTitle: 'Wir konnten dich nicht abmelden',
    signOutFailedDescription:
      'Beim Beenden deiner Sitzung ist etwas schiefgelaufen. Bitte versuche es erneut.',
    tryAgain: 'Erneut versuchen',
    goBackHome: 'Zur Startseite',
    goBack: 'Zurück',
    termsOfUse: 'Nutzungsbedingungen',
    privacyPolicy: 'Datenschutzrichtlinie',
    createYourAccount: 'Konto erstellen',
    enterYourEmailToContinue: 'Gib deine E-Mail-Adresse ein, um fortzufahren.',
    setNamePasswordToContinue:
      'Lege deinen Namen und ein Passwort fest, um fortzufahren.',
    enterFullNameAndPassword:
      'Gib deinen vollständigen Namen ein und lege ein Passwort fest, um fortzufahren',
    continueButton: 'Weiter',
    alreadyHaveAccount: 'Hast du bereits ein Konto?',
    emailAddressLabel: 'E-Mail-Adresse',
    fullName: 'Vollständiger Name',
    confirmPasswordLabel: 'Passwort bestätigen',
    createPasswordPlaceholder: 'Erstelle ein Passwort',
    reEnterPasswordPlaceholder: 'Passwort erneut eingeben',
    passwordMinimumHint: 'Das Passwort muss mindestens 6 Zeichen lang sein.',
    websiteLanguage: 'Website-Sprache',
    websiteLanguageOptional:
      'Optional. Du kannst dies später in den Einstellungen ändern.',
    chooseLanguageLater: 'Später',
    selectLanguage: 'Sprache auswählen',
    searchLanguages: 'Sprachen suchen…',
    noLanguagesFound: 'Keine Sprachen gefunden.',
  }),
  ja: mergeAuthCopy({
    welcomeBack: 'おかえりなさい',
    letsGetYouBackIn: 'ログインしましょう。',
    email: 'メール',
    emailPlaceholder: 'you@email.example',
    password: 'パスワード',
    forgotPassword: 'パスワードをお忘れですか？',
    signIn: 'ログイン',
    signingIn: 'ログイン中...',
    orContinueWith: 'または次で続行',
    continueWithGoogle: 'Googleで続行',
    dontHaveAccount: 'アカウントをお持ちでないですか？',
    signUp: '登録',
    signingYouOut: 'ログアウト中',
    signOutWait: '安全にセッションを終了しています。',
    signOutFailedTitle: 'ログアウトできませんでした',
    signOutFailedDescription:
      'セッションの終了中に問題が発生しました。もう一度お試しください。',
    tryAgain: '再試行',
    goBackHome: 'ホームへ戻る',
    goBack: '戻る',
    termsOfUse: '利用規約',
    privacyPolicy: 'プライバシーポリシー',
    createYourAccount: 'アカウントを作成',
    enterYourEmailToContinue: '続けるにはメールアドレスを入力してください。',
    setNamePasswordToContinue: '続けるには名前とパスワードを設定してください。',
    enterFullNameAndPassword:
      '続けるにはフルネームとパスワードを設定してください',
    continueButton: '続行',
    alreadyHaveAccount: 'すでにアカウントをお持ちですか？',
    emailAddressLabel: 'メールアドレス',
    fullName: 'フルネーム',
    confirmPasswordLabel: 'パスワードの確認',
    createPasswordPlaceholder: 'パスワードを作成',
    reEnterPasswordPlaceholder: 'パスワードを再入力',
    passwordMinimumHint: 'パスワードは6文字以上で入力してください',
    websiteLanguage: 'ウェブサイトの言語',
    websiteLanguageOptional: '今は任意です。後で設定から変更できます。',
    chooseLanguageLater: '後で',
    selectLanguage: '言語を選択',
    searchLanguages: '言語を検索…',
    noLanguagesFound: '言語が見つかりません。',
  }),
  hi: mergeAuthCopy({
    welcomeBack: 'वापसी पर स्वागत है',
    letsGetYouBackIn: 'चलिए आपको फिर से साइन इन कराते हैं।',
    email: 'ईमेल',
    emailPlaceholder: 'you@email.example',
    password: 'पासवर्ड',
    forgotPassword: 'पासवर्ड भूल गए?',
    signIn: 'साइन इन',
    signingIn: 'साइन इन हो रहा है...',
    checkYourEmail: 'अपना ईमेल देखें',
    verificationCodeSent: 'हमने सत्यापन कोड भेजा है:',
    pleaseVerifyEmail: 'जारी रखने के लिए कृपया अपना ईमेल सत्यापित करें',
    verificationCode: 'सत्यापन कोड',
    verifying: 'सत्यापन हो रहा है...',
    verifyAndSignIn: 'सत्यापित करें और साइन इन करें',
    resendCode: 'कोड दोबारा भेजें',
    backToSignIn: 'साइन इन पर वापस जाएँ',
    orContinueWith: 'या इसके साथ जारी रखें',
    continueWithGoogle: 'Google के साथ जारी रखें',
    dontHaveAccount: 'खाता नहीं है?',
    signUp: 'साइन अप',
    signingYouOut: 'आपको साइन आउट किया जा रहा है',
    signOutWait: 'आपका सत्र सुरक्षित रूप से समाप्त किया जा रहा है।',
    signOutFailedTitle: 'हम आपको साइन आउट नहीं कर सके',
    signOutFailedDescription:
      'सत्र समाप्त करते समय कुछ गलत हुआ। कृपया फिर से कोशिश करें।',
    tryAgain: 'फिर कोशिश करें',
    goBackHome: 'होम पर वापस जाएँ',
    goBack: 'वापस जाएं',
    termsOfUse: 'उपयोग की शर्तें',
    privacyPolicy: 'गोपनीयता नीति',
    createYourAccount: 'अपना अकाउंट बनाएं',
    enterYourEmailToContinue: 'जारी रखने के लिए अपना ईमेल डालें।',
    setNamePasswordToContinue: 'जारी रखने के लिए नाम और पासवर्ड सेट करें।',
    enterFullNameAndPassword:
      'जारी रखने के लिए अपना पूरा नाम डालें और पासवर्ड सेट करें',
    continueButton: 'जारी रखें',
    alreadyHaveAccount: 'क्या आपके पास पहले से अकाउंट है?',
    emailAddressLabel: 'ईमेल पता',
    fullName: 'पूरा नाम',
    confirmPasswordLabel: 'पासवर्ड की पुष्टि करें',
    createPasswordPlaceholder: 'एक पासवर्ड बनाएं',
    reEnterPasswordPlaceholder: 'अपना पासवर्ड दोबारा डालें',
    passwordMinimumHint: 'पासवर्ड कम से कम 6 अक्षरों का होना चाहिए',
    websiteLanguage: 'वेबसाइट की भाषा',
    websiteLanguageOptional:
      'यह अभी वैकल्पिक है। आप इसे बाद में सेटिंग्स में भी बदल सकते हैं।',
    chooseLanguageLater: 'बाद में',
    selectLanguage: 'भाषा चुनें',
    searchLanguages: 'भाषाएँ खोजें…',
    noLanguagesFound: 'कोई भाषा नहीं मिली।',
    enterVerificationCode: 'सत्यापन कोड दर्ज करें',
  }),
  ru: mergeAuthCopy({
    welcomeBack: 'С возвращением',
    letsGetYouBackIn: 'Давайте войдём в аккаунт.',
    email: 'Email',
    emailPlaceholder: 'you@email.example',
    password: 'Пароль',
    forgotPassword: 'Забыли пароль?',
    signIn: 'Войти',
    signingIn: 'Вход...',
    orContinueWith: 'ИЛИ ПРОДОЛЖИТЬ С',
    continueWithGoogle: 'Продолжить с Google',
    dontHaveAccount: 'Нет аккаунта?',
    signUp: 'Зарегистрироваться',
    signingYouOut: 'Выход из аккаунта',
    signOutWait: 'Подождите, мы безопасно завершаем вашу сессию.',
    signOutFailedTitle: 'Не удалось выйти',
    signOutFailedDescription:
      'При завершении сессии что-то пошло не так. Попробуйте ещё раз.',
    tryAgain: 'Повторить',
    goBackHome: 'Вернуться на главную',
    goBack: 'Назад',
    termsOfUse: 'Условия использования',
    privacyPolicy: 'Политика конфиденциальности',
    createYourAccount: 'Создайте аккаунт',
    enterYourEmailToContinue: 'Введите email, чтобы продолжить.',
    setNamePasswordToContinue: 'Укажите имя и пароль, чтобы продолжить.',
    enterFullNameAndPassword:
      'Введите полное имя и задайте пароль, чтобы продолжить',
    continueButton: 'Продолжить',
    alreadyHaveAccount: 'Уже есть аккаунт?',
    emailAddressLabel: 'Адрес электронной почты',
    fullName: 'Полное имя',
    confirmPasswordLabel: 'Подтвердите пароль',
    createPasswordPlaceholder: 'Создайте пароль',
    reEnterPasswordPlaceholder: 'Введите пароль ещё раз',
    passwordMinimumHint: 'Пароль должен содержать не менее 6 символов',
    websiteLanguage: 'Язык сайта',
    websiteLanguageOptional:
      'Необязательно. Позже это можно изменить в настройках.',
    chooseLanguageLater: 'Позже',
    selectLanguage: 'Выберите язык',
    searchLanguages: 'Поиск языков…',
    noLanguagesFound: 'Языки не найдены.',
  }),
  zh: mergeAuthCopy({
    welcomeBack: '欢迎回来',
    letsGetYouBackIn: '让我们帮你登录。',
    email: '邮箱',
    emailPlaceholder: 'you@email.example',
    password: '密码',
    forgotPassword: '忘记密码？',
    signIn: '登录',
    signingIn: '正在登录...',
    orContinueWith: '或使用以下方式继续',
    continueWithGoogle: '使用 Google 继续',
    dontHaveAccount: '还没有账号？',
    signUp: '注册',
    signingYouOut: '正在退出登录',
    signOutWait: '请稍等，我们正在安全结束你的会话。',
    signOutFailedTitle: '无法退出登录',
    signOutFailedDescription: '结束会话时出现问题。请重试。',
    tryAgain: '重试',
    goBackHome: '返回首页',
    goBack: '返回',
    termsOfUse: '使用条款',
    privacyPolicy: '隐私政策',
    createYourAccount: '创建账号',
    enterYourEmailToContinue: '输入你的电子邮箱以继续。',
    setNamePasswordToContinue: '设置你的姓名和密码以继续。',
    enterFullNameAndPassword: '输入你的全名并设置密码以继续',
    continueButton: '继续',
    alreadyHaveAccount: '已有账号？',
    emailAddressLabel: '电子邮箱地址',
    fullName: '全名',
    confirmPasswordLabel: '确认密码',
    createPasswordPlaceholder: '创建密码',
    reEnterPasswordPlaceholder: '再次输入你的密码',
    passwordMinimumHint: '密码至少需要 6 个字符',
    websiteLanguage: '网站语言',
    websiteLanguageOptional: '目前可选。你也可以稍后在设置中更改。',
    chooseLanguageLater: '稍后',
    selectLanguage: '选择语言',
    searchLanguages: '搜索语言…',
    noLanguagesFound: '未找到语言。',
  }),
  'zh-TW': mergeAuthCopy({
    welcomeBack: '歡迎回來',
    letsGetYouBackIn: '讓我們幫你登入。',
    email: '電子郵件',
    emailPlaceholder: 'you@email.example',
    password: '密碼',
    forgotPassword: '忘記密碼？',
    signIn: '登入',
    signingIn: '正在登入...',
    orContinueWith: '或使用以下方式繼續',
    continueWithGoogle: '使用 Google 繼續',
    dontHaveAccount: '還沒有帳號？',
    signUp: '註冊',
    signingYouOut: '正在登出',
    signOutWait: '請稍候，我們正在安全結束你的工作階段。',
    signOutFailedTitle: '無法登出',
    signOutFailedDescription: '結束工作階段時發生問題。請再試一次。',
    tryAgain: '再試一次',
    goBackHome: '返回首頁',
    goBack: '返回',
    termsOfUse: '使用條款',
    privacyPolicy: '隱私權政策',
    createYourAccount: '建立帳號',
    enterYourEmailToContinue: '輸入你的電子郵件以繼續。',
    setNamePasswordToContinue: '設定你的姓名與密碼以繼續。',
    enterFullNameAndPassword: '輸入你的全名並設定密碼以繼續',
    continueButton: '繼續',
    alreadyHaveAccount: '已有帳號？',
    emailAddressLabel: '電子郵件地址',
    fullName: '全名',
    confirmPasswordLabel: '確認密碼',
    createPasswordPlaceholder: '建立密碼',
    reEnterPasswordPlaceholder: '再次輸入你的密碼',
    passwordMinimumHint: '密碼至少需要 6 個字元',
    websiteLanguage: '網站語言',
    websiteLanguageOptional: '目前可選。你也可以稍後在設定中變更。',
    chooseLanguageLater: '稍後',
    selectLanguage: '選擇語言',
    searchLanguages: '搜尋語言…',
    noLanguagesFound: '找不到語言。',
  }),
  vi: VI_COPY,
  id: mergeAuthCopy({
    welcomeBack: 'Selamat datang kembali',
    letsGetYouBackIn: 'Mari masuk kembali.',
    email: 'Email',
    emailPlaceholder: 'anda@imai.example',
    password: 'Kata sandi',
    forgotPassword: 'Lupa kata sandi?',
    signIn: 'Masuk',
    signingIn: 'Masuk...',
    orContinueWith: 'ATAU LANJUTKAN DENGAN',
    continueWithGoogle: 'Lanjutkan dengan Google',
    dontHaveAccount: 'Belum punya akun?',
    signUp: 'Daftar',
    signingYouOut: 'Mengeluarkan Anda',
    signOutWait: 'Tunggu sebentar sementara kami mengakhiri sesi Anda.',
    signOutFailedTitle: 'Kami tidak dapat mengeluarkan Anda',
    signOutFailedDescription:
      'Terjadi kesalahan saat mengakhiri sesi Anda. Silakan coba lagi.',
    tryAgain: 'Coba lagi',
    goBackHome: 'Kembali ke beranda',
    goBack: 'Kembali',
    termsOfUse: 'Ketentuan Penggunaan',
    privacyPolicy: 'Kebijakan Privasi',
    createYourAccount: 'Buat akun',
    enterYourEmailToContinue: 'Masukkan emailmu untuk melanjutkan.',
    setNamePasswordToContinue: 'Atur nama dan kata sandi untuk melanjutkan.',
    enterFullNameAndPassword:
      'Masukkan nama lengkap dan atur kata sandi untuk melanjutkan',
    continueButton: 'Lanjut',
    alreadyHaveAccount: 'Sudah punya akun?',
    emailAddressLabel: 'Alamat email',
    fullName: 'Nama lengkap',
    confirmPasswordLabel: 'Konfirmasi kata sandi',
    createPasswordPlaceholder: 'Buat kata sandi',
    reEnterPasswordPlaceholder: 'Masukkan kembali kata sandi',
    passwordMinimumHint: 'Kata sandi harus terdiri dari minimal 6 karakter',
    websiteLanguage: 'Bahasa situs',
    websiteLanguageOptional:
      'Opsional untuk saat ini. Anda dapat mengubahnya nanti di pengaturan.',
    chooseLanguageLater: 'Nanti',
    selectLanguage: 'Pilih bahasa',
    searchLanguages: 'Cari bahasa…',
    noLanguagesFound: 'Bahasa tidak ditemukan.',
  }),
};

type AuthVerificationCopy = Pick<
  AuthPageCopy,
  | 'enterSixDigitCode'
  | 'invalidVerificationCode'
  | 'verificationCodeExpired'
  | 'invalidCredentials'
  | 'emailVerifiedEnterPassword'
  | 'emailVerifiedSignInAgain'
  | 'invalidPasswordTryAgain'
  | 'signInAfterVerificationFailed'
  | 'verificationFailed'
  | 'accountCreatedCheckEmail'
  | 'verificationCodeResentSuccess'
  | 'accountDeletionPending'
>;

const AUTH_VERIFICATION_COPY: Record<SupportedLocale, AuthVerificationCopy> = {
  en: {
    enterSixDigitCode: 'Enter the 6-digit code',
    invalidVerificationCode: 'Invalid verification code',
    verificationCodeExpired:
      'This verification code has expired. Request a new one.',
    invalidCredentials:
      'Invalid email or password. Check your credentials and try again.',
    emailVerifiedEnterPassword:
      'Email verified. Enter your password again to finish signing in.',
    emailVerifiedSignInAgain:
      'Email verified. Sign in once more to finish logging in.',
    invalidPasswordTryAgain:
      'Invalid password. Check the requirements and try again.',
    signInAfterVerificationFailed: 'Sign in failed after verification',
    verificationFailed: 'Verification failed',
    accountCreatedCheckEmail:
      'Account created. Enter the 6-digit code sent to your email.',
    verificationCodeResentSuccess: 'New verification code sent.',
    accountDeletionPending:
      'Your account deletion is still being completed. Contact support if this takes more than two days.',
  },
  'pt-BR': {
    enterSixDigitCode: 'Digite o c\u00F3digo de 6 d\u00EDgitos.',
    invalidVerificationCode:
      'C\u00F3digo de verifica\u00E7\u00E3o inv\u00E1lido.',
    verificationCodeExpired:
      'Este c\u00F3digo de verifica\u00E7\u00E3o expirou. Pe\u00E7a um novo.',
    invalidCredentials:
      'E-mail ou senha inv\u00E1lidos. Verifique suas credenciais e tente novamente.',
    emailVerifiedEnterPassword:
      'E-mail verificado. Digite sua senha novamente para terminar de assinar.',
    emailVerifiedSignInAgain:
      'E-mail verificado. Assine mais uma vez para terminar de fazer login.',
    invalidPasswordTryAgain:
      'Senha inv\u00E1lida. Verifique os requisitos e tente novamente.',
    signInAfterVerificationFailed:
      'O registro falhou ap\u00F3s a verifica\u00E7\u00E3o.',
    verificationFailed: 'A verifica\u00E7\u00E3o falhou.',
    accountCreatedCheckEmail:
      'Conta criada. Digite o c\u00F3digo de 6 d\u00EDgitos enviado para seu e-mail.',
    verificationCodeResentSuccess:
      'Novo c\u00F3digo de verifica\u00E7\u00E3o enviado.',
    accountDeletionPending:
      'A exclusão da sua conta ainda está sendo concluída. Fale com o suporte se demorar mais de dois dias.',
  },
  ar: {
    enterSixDigitCode: 'أدخل الرمز المكوّن من 6 أرقام',
    invalidVerificationCode: 'رمز التحقق غير صحيح',
    verificationCodeExpired: 'انتهت صلاحية رمز التحقق. اطلب رمزًا جديدًا.',
    invalidCredentials:
      'البريد الإلكتروني أو كلمة المرور غير صحيحة. تحقّق من بياناتك وحاول مرة أخرى.',
    emailVerifiedEnterPassword:
      'تم التحقق من البريد الإلكتروني. أدخل كلمة المرور مرة أخرى لإكمال تسجيل الدخول.',
    emailVerifiedSignInAgain:
      'تم التحقق من البريد الإلكتروني. سجّل الدخول مرة أخرى لإكمال العملية.',
    invalidPasswordTryAgain:
      'كلمة المرور غير صحيحة. تحقّق من المتطلبات وحاول مرة أخرى.',
    signInAfterVerificationFailed: 'تعذّر تسجيل الدخول بعد التحقق',
    verificationFailed: 'تعذّر التحقق',
    accountCreatedCheckEmail:
      'تم إنشاء الحساب. أدخل الرمز المكوّن من 6 أرقام الذي أُرسل إلى بريدك الإلكتروني.',
    verificationCodeResentSuccess: 'تم إرسال رمز تحقق جديد.',
    accountDeletionPending:
      'لا يزال حذف حسابك قيد الإكمال. تواصل مع الدعم إذا استغرق ذلك أكثر من يومين.',
  },
  es: {
    enterSixDigitCode: 'Introduce el código de 6 dígitos',
    invalidVerificationCode: 'El código de verificación no es válido',
    verificationCodeExpired:
      'El código de verificación ha caducado. Solicita uno nuevo.',
    invalidCredentials:
      'El email o la contraseña no son válidos. Revisa tus datos e inténtalo de nuevo.',
    emailVerifiedEnterPassword:
      'Email verificado. Introduce de nuevo tu contraseña para terminar de iniciar sesión.',
    emailVerifiedSignInAgain:
      'Email verificado. Inicia sesión una vez más para completar el acceso.',
    invalidPasswordTryAgain:
      'La contraseña no es válida. Revisa los requisitos e inténtalo de nuevo.',
    signInAfterVerificationFailed:
      'No se pudo iniciar sesión después de la verificación',
    verificationFailed: 'No se pudo completar la verificación',
    accountCreatedCheckEmail:
      'Cuenta creada. Introduce el código de 6 dígitos que enviamos a tu email.',
    verificationCodeResentSuccess:
      'Se ha enviado un nuevo código de verificación.',
    accountDeletionPending:
      'Todavía estamos terminando de eliminar tu cuenta. Contacta con soporte si tarda más de dos días.',
  },
  fr: {
    enterSixDigitCode: 'Saisissez le code à 6 chiffres',
    invalidVerificationCode: 'Le code de vérification est incorrect',
    verificationCodeExpired:
      'Le code de vérification a expiré. Demandez-en un nouveau.',
    invalidCredentials:
      'Adresse e-mail ou mot de passe incorrect. Vérifiez vos informations et réessayez.',
    emailVerifiedEnterPassword:
      'Adresse e-mail vérifiée. Saisissez à nouveau votre mot de passe pour terminer la connexion.',
    emailVerifiedSignInAgain:
      'Adresse e-mail vérifiée. Connectez-vous une dernière fois pour terminer.',
    invalidPasswordTryAgain:
      'Mot de passe incorrect. Vérifiez les critères et réessayez.',
    signInAfterVerificationFailed:
      'La connexion a échoué après la vérification',
    verificationFailed: 'La vérification a échoué',
    accountCreatedCheckEmail:
      'Compte créé. Saisissez le code à 6 chiffres envoyé à votre adresse e-mail.',
    verificationCodeResentSuccess:
      'Un nouveau code de vérification a été envoyé.',
    accountDeletionPending:
      'La suppression de votre compte est en cours de finalisation. Contactez l’assistance si cela prend plus de deux jours.',
  },
  it: {
    enterSixDigitCode: 'Inserisci il codice di 6 cifre',
    invalidVerificationCode: 'Il codice di verifica non è valido',
    verificationCodeExpired:
      'Il codice di verifica è scaduto. Richiedine uno nuovo.',
    invalidCredentials:
      'Email o password non valide. Controlla i dati e riprova.',
    emailVerifiedEnterPassword:
      'Email verificata. Inserisci di nuovo la password per completare l’accesso.',
    emailVerifiedSignInAgain:
      'Email verificata. Accedi ancora una volta per completare l’operazione.',
    invalidPasswordTryAgain:
      'Password non valida. Controlla i requisiti e riprova.',
    signInAfterVerificationFailed: 'Accesso non riuscito dopo la verifica',
    verificationFailed: 'Verifica non riuscita',
    accountCreatedCheckEmail:
      'Account creato. Inserisci il codice di 6 cifre inviato alla tua email.',
    verificationCodeResentSuccess:
      'È stato inviato un nuovo codice di verifica.',
    accountDeletionPending:
      'L’eliminazione del tuo account è ancora in corso. Contatta l’assistenza se richiede più di due giorni.',
  },
  ko: {
    enterSixDigitCode: '6자리 코드를 입력하세요',
    invalidVerificationCode: '인증 코드가 올바르지 않습니다',
    verificationCodeExpired:
      '인증 코드가 만료되었습니다. 새 코드를 요청하세요.',
    invalidCredentials:
      '이메일 또는 비밀번호가 올바르지 않습니다. 입력 내용을 확인하고 다시 시도하세요.',
    emailVerifiedEnterPassword:
      '이메일 인증이 완료되었습니다. 로그인을 마치려면 비밀번호를 다시 입력하세요.',
    emailVerifiedSignInAgain:
      '이메일 인증이 완료되었습니다. 한 번 더 로그인해 주세요.',
    invalidPasswordTryAgain:
      '비밀번호가 올바르지 않습니다. 요구 사항을 확인하고 다시 시도하세요.',
    signInAfterVerificationFailed: '인증 후 로그인하지 못했습니다',
    verificationFailed: '인증하지 못했습니다',
    accountCreatedCheckEmail:
      '계정이 생성되었습니다. 이메일로 보낸 6자리 코드를 입력하세요.',
    verificationCodeResentSuccess: '새 인증 코드를 보냈습니다.',
    accountDeletionPending:
      '계정 삭제가 아직 진행 중입니다. 이틀 이상 걸리면 지원팀에 문의하세요.',
  },
  de: {
    enterSixDigitCode: 'Gib den 6-stelligen Code ein',
    invalidVerificationCode: 'Der Bestätigungscode ist ungültig',
    verificationCodeExpired:
      'Der Bestätigungscode ist abgelaufen. Fordere einen neuen an.',
    invalidCredentials:
      'E-Mail-Adresse oder Passwort ist falsch. Prüfe deine Angaben und versuche es erneut.',
    emailVerifiedEnterPassword:
      'E-Mail-Adresse bestätigt. Gib dein Passwort erneut ein, um die Anmeldung abzuschließen.',
    emailVerifiedSignInAgain:
      'E-Mail-Adresse bestätigt. Melde dich noch einmal an, um den Vorgang abzuschließen.',
    invalidPasswordTryAgain:
      'Das Passwort ist falsch. Prüfe die Anforderungen und versuche es erneut.',
    signInAfterVerificationFailed:
      'Anmeldung nach der Bestätigung fehlgeschlagen',
    verificationFailed: 'Bestätigung fehlgeschlagen',
    accountCreatedCheckEmail:
      'Konto erstellt. Gib den 6-stelligen Code ein, den wir dir per E-Mail gesendet haben.',
    verificationCodeResentSuccess: 'Ein neuer Bestätigungscode wurde gesendet.',
    accountDeletionPending:
      'Die Löschung deines Kontos wird noch abgeschlossen. Wende dich an den Support, wenn das länger als zwei Tage dauert.',
  },
  ja: {
    enterSixDigitCode: '6桁のコードを入力してください',
    invalidVerificationCode: '確認コードが正しくありません',
    verificationCodeExpired:
      '確認コードの有効期限が切れました。新しいコードをリクエストしてください。',
    invalidCredentials:
      'メールアドレスまたはパスワードが正しくありません。入力内容を確認して、もう一度お試しください。',
    emailVerifiedEnterPassword:
      'メールアドレスを確認しました。ログインを完了するため、パスワードをもう一度入力してください。',
    emailVerifiedSignInAgain:
      'メールアドレスを確認しました。ログインを完了するため、もう一度サインインしてください。',
    invalidPasswordTryAgain:
      'パスワードが正しくありません。要件を確認して、もう一度お試しください。',
    signInAfterVerificationFailed: '確認後のログインに失敗しました',
    verificationFailed: '確認に失敗しました',
    accountCreatedCheckEmail:
      'アカウントを作成しました。メールで届いた6桁のコードを入力してください。',
    verificationCodeResentSuccess: '新しい確認コードを送信しました。',
    accountDeletionPending:
      'アカウントの削除はまだ完了処理中です。2日以上かかる場合はサポートにお問い合わせください。',
  },
  hi: {
    enterSixDigitCode: '6 अंकों का कोड दर्ज करें',
    invalidVerificationCode: 'सत्यापन कोड सही नहीं है',
    verificationCodeExpired:
      'सत्यापन कोड की समय-सीमा समाप्त हो गई है। नया कोड मँगाएँ।',
    invalidCredentials:
      'ईमेल या पासवर्ड सही नहीं है। अपनी जानकारी जाँचें और फिर कोशिश करें।',
    emailVerifiedEnterPassword:
      'ईमेल सत्यापित हो गया है। साइन इन पूरा करने के लिए पासवर्ड फिर से दर्ज करें।',
    emailVerifiedSignInAgain:
      'ईमेल सत्यापित हो गया है। लॉग इन पूरा करने के लिए एक बार फिर साइन इन करें।',
    invalidPasswordTryAgain:
      'पासवर्ड सही नहीं है। ज़रूरी शर्तें जाँचें और फिर कोशिश करें।',
    signInAfterVerificationFailed: 'सत्यापन के बाद साइन इन नहीं हो सका',
    verificationFailed: 'सत्यापन नहीं हो सका',
    accountCreatedCheckEmail:
      'अकाउंट बन गया है। ईमेल पर भेजा गया 6 अंकों का कोड दर्ज करें।',
    verificationCodeResentSuccess: 'नया सत्यापन कोड भेज दिया गया है।',
    accountDeletionPending:
      'आपका खाता हटाने का काम अभी पूरा हो रहा है। अगर इसमें दो दिन से ज़्यादा लगे तो सहायता टीम से संपर्क करें।',
  },
  ru: {
    enterSixDigitCode: 'Введите 6-значный код',
    invalidVerificationCode: 'Неверный код подтверждения',
    verificationCodeExpired: 'Срок действия кода истёк. Запросите новый код.',
    invalidCredentials:
      'Неверный email или пароль. Проверьте данные и попробуйте снова.',
    emailVerifiedEnterPassword:
      'Email подтверждён. Введите пароль ещё раз, чтобы завершить вход.',
    emailVerifiedSignInAgain:
      'Email подтверждён. Войдите ещё раз, чтобы завершить авторизацию.',
    invalidPasswordTryAgain:
      'Неверный пароль. Проверьте требования и попробуйте снова.',
    signInAfterVerificationFailed: 'Не удалось войти после подтверждения',
    verificationFailed: 'Не удалось подтвердить email',
    accountCreatedCheckEmail:
      'Аккаунт создан. Введите 6-значный код, отправленный на ваш email.',
    verificationCodeResentSuccess: 'Новый код подтверждения отправлен.',
    accountDeletionPending:
      'Удаление вашего аккаунта ещё завершается. Если это займёт больше двух суток, обратитесь в поддержку.',
  },
  zh: {
    enterSixDigitCode: '请输入 6 位验证码',
    invalidVerificationCode: '验证码不正确',
    verificationCodeExpired: '验证码已过期。请重新获取。',
    invalidCredentials: '邮箱或密码不正确。请检查后重试。',
    emailVerifiedEnterPassword: '邮箱已验证。请再次输入密码以完成登录。',
    emailVerifiedSignInAgain: '邮箱已验证。请再次登录以完成操作。',
    invalidPasswordTryAgain: '密码不正确。请检查要求后重试。',
    signInAfterVerificationFailed: '验证后登录失败',
    verificationFailed: '验证失败',
    accountCreatedCheckEmail: '账号已创建。请输入发送到邮箱的 6 位验证码。',
    verificationCodeResentSuccess: '新的验证码已发送。',
    accountDeletionPending:
      '你的账号删除仍在完成中。如果超过两天仍未完成，请联系支持团队。',
  },
  'zh-TW': {
    enterSixDigitCode: '請輸入 6 位數驗證碼',
    invalidVerificationCode: '驗證碼不正確',
    verificationCodeExpired: '驗證碼已過期。請重新取得。',
    invalidCredentials: '電子郵件或密碼不正確。請檢查後再試一次。',
    emailVerifiedEnterPassword: '電子郵件已驗證。請再次輸入密碼以完成登入。',
    emailVerifiedSignInAgain: '電子郵件已驗證。請再次登入以完成操作。',
    invalidPasswordTryAgain: '密碼不正確。請檢查要求後再試一次。',
    signInAfterVerificationFailed: '驗證後無法登入',
    verificationFailed: '驗證失敗',
    accountCreatedCheckEmail:
      '帳號已建立。請輸入傳送到電子郵件的 6 位數驗證碼。',
    verificationCodeResentSuccess: '新的驗證碼已傳送。',
    accountDeletionPending:
      '你的帳號刪除仍在完成中。如果超過兩天仍未完成，請聯絡支援團隊。',
  },
  vi: {
    enterSixDigitCode: 'Nhập mã gồm 6 chữ số',
    invalidVerificationCode: 'Mã xác minh không đúng',
    verificationCodeExpired: 'Mã xác minh đã hết hạn. Hãy yêu cầu mã mới.',
    invalidCredentials:
      'Email hoặc mật khẩu không đúng. Hãy kiểm tra lại rồi thử lần nữa.',
    emailVerifiedEnterPassword:
      'Email đã được xác minh. Nhập lại mật khẩu để hoàn tất đăng nhập.',
    emailVerifiedSignInAgain:
      'Email đã được xác minh. Đăng nhập thêm một lần để hoàn tất.',
    invalidPasswordTryAgain:
      'Mật khẩu không đúng. Hãy kiểm tra yêu cầu rồi thử lại.',
    signInAfterVerificationFailed: 'Không thể đăng nhập sau khi xác minh',
    verificationFailed: 'Xác minh không thành công',
    accountCreatedCheckEmail:
      'Tài khoản đã được tạo. Nhập mã 6 chữ số được gửi đến email của bạn.',
    verificationCodeResentSuccess: 'Đã gửi mã xác minh mới.',
    accountDeletionPending:
      'Việc xóa tài khoản của bạn vẫn đang được hoàn tất. Hãy liên hệ bộ phận hỗ trợ nếu việc này mất hơn hai ngày.',
  },
  id: {
    enterSixDigitCode: 'Masukkan kode 6 digit',
    invalidVerificationCode: 'Kode verifikasi tidak valid',
    verificationCodeExpired:
      'Kode verifikasi telah kedaluwarsa. Minta kode baru.',
    invalidCredentials:
      'Email atau kata sandi tidak valid. Periksa kembali lalu coba lagi.',
    emailVerifiedEnterPassword:
      'Email telah diverifikasi. Masukkan kembali kata sandi untuk menyelesaikan proses masuk.',
    emailVerifiedSignInAgain:
      'Email telah diverifikasi. Masuk sekali lagi untuk menyelesaikan proses.',
    invalidPasswordTryAgain:
      'Kata sandi tidak valid. Periksa persyaratannya lalu coba lagi.',
    signInAfterVerificationFailed: 'Gagal masuk setelah verifikasi',
    verificationFailed: 'Verifikasi gagal',
    accountCreatedCheckEmail:
      'Akun telah dibuat. Masukkan kode 6 digit yang dikirim ke email Anda.',
    verificationCodeResentSuccess: 'Kode verifikasi baru telah dikirim.',
    accountDeletionPending:
      'Penghapusan akun Anda masih diselesaikan. Hubungi dukungan jika ini memakan waktu lebih dari dua hari.',
  },
};

export function getAuthPageCopy(locale: string): AuthPageCopy {
  const normalizedLocale = normalizeLocale(locale);
  return {
    ...(COPY_BY_LOCALE[normalizedLocale] ?? EN_COPY),
    ...getAuthFlowCopy(normalizedLocale),
    ...AUTH_VERIFICATION_COPY[normalizedLocale],
    ...getPasswordResetValidationCopy(normalizedLocale),
  };
}
